import React, { useState } from 'react';
import {
  Button,
  Group,
  Stack,
  Box,
  Text,
  Paper,
  Title,
  Card,
  Badge,
  Divider,
  Transition
} from '@mantine/core';

// Import your existing components
import CustomerSearchDialog from './CustomerSearchDialog';
import HeaderGrid from './HeaderGrid';
import LineGrid from './LineGrid';

// Wrapper component for the HeaderGrid to add navigation
const QuoteHeaderScreen = ({ customer, onBack, onSave }) => {
  return (
    <Box>
      <Paper p="lg" shadow="sm" mb="md">
        <Stack spacing="md">
          <Group position="apart" align="center">
            <Title order={2}>Create New Quote</Title>
            <Badge color="blue" size="lg">Step 2: Header Details</Badge>
          </Group>
          
          <Group position="apart" align="center">
            {/* Selected customer info on left side */}
            <Group spacing="md" align="center">
              <Text size="sm" color="blue" weight={500}>Selected:</Text>
              <Box>
                <Text weight={600}>{customer.customerName}</Text>
                <Text size="xs" color="dimmed">{customer.customerNumber} • {customer.region}</Text>
              </Box>
            </Group>
            
            {/* Actions on right side */}
            <Group spacing="sm">
              <Button variant="default" onClick={onBack}>
                Back to Customer Selection
              </Button>
              <Button variant="default">
                Save Draft
              </Button>
              <Button onClick={onSave}>
                Continue to Line Items
              </Button>
            </Group>
          </Group>
        </Stack>
      </Paper>

      {/* Your existing HeaderGrid component */}
      <HeaderGrid customer={customer} />
    </Box>
  );
};

// Line items screen (step 3)
const QuoteLineItemsScreen = ({ customer, onBack, onSave }) => {
  const [headerCollapsed, setHeaderCollapsed] = useState(false);

  // Auto-collapse header after animation completes
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setHeaderCollapsed(true);
    }, 1000); // Collapse after slide animations complete
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Full header at top (same as step 2 but with different navigation) */}
      <Transition mounted={true} transition="slide-down" duration={600} timingFunction="ease-out">
        {(styles) => (
          <div style={styles}>
            <Box>
              <Paper p="lg" shadow="sm" mb="md">
                <Stack spacing="md">
                  <Group position="apart" align="center">
                    <Title order={2}>Create New Quote</Title>
                    <Badge color="green" size="lg">Step 3: Line Items</Badge>
                  </Group>
                  
                  <Group position="apart" align="center">
                    {/* Selected customer info on left side */}
                    <Group spacing="md" align="center">
                      <Text size="sm" color="blue" weight={500}>Selected:</Text>
                      <Box>
                        <Text weight={600}>{customer.customerName}</Text>
                        <Text size="xs" color="dimmed">{customer.customerNumber} • {customer.region}</Text>
                      </Box>
                    </Group>
                    
                    {/* Actions on right side */}
                    <Group spacing="sm">
                      <Button variant="default" onClick={onBack}>
                        Edit Header
                      </Button>
                      <Button variant="default">
                        Save Draft
                      </Button>
                      <Button onClick={onSave}>
                        Save Quote
                      </Button>
                    </Group>
                  </Group>
                </Stack>
              </Paper>

              {/* HeaderGrid with external collapsed state control */}
              <HeaderGrid 
                customer={customer} 
                collapsed={headerCollapsed}
                onToggleCollapse={() => setHeaderCollapsed(!headerCollapsed)}
              />
            </Box>
          </div>
        )}
      </Transition>

      {/* Line items slide in below the header */}
      <Transition mounted={true} transition="slide-up" duration={800} timingFunction="ease-out">
        {(styles) => (
          <Box style={{ ...styles, padding: '16px', paddingTop: '0' }}>
            <LineGrid />
          </Box>
        )}
      </Transition>
    </Box>
  );
};

// Main Quote Creation Wizard Component
const QuoteCreationWizard = () => {
  const [currentStep, setCurrentStep] = useState('customer-selection');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
  };

  const handleBackToCustomerSelection = () => {
    setCurrentStep('customer-selection');
    setSelectedCustomer(null);
  };

  const handleContinueToLineItems = () => {
    setCurrentStep('line-items');
  };

  const handleBackToHeader = () => {
    setCurrentStep('quote-creation');
  };

  const handleSaveQuote = () => {
    alert(`Quote completed for customer: ${selectedCustomer.customerName}!\n\nQuote has been saved with all line items.`);
    // Could navigate to quotes list or show confirmation
  };

  const handleStartNewQuote = () => {
    setCurrentStep('customer-selection');
    setSelectedCustomer(null);
  };

  // Step 3: Line Items
  if (currentStep === 'line-items' && selectedCustomer) {
    return (
      <QuoteLineItemsScreen 
        customer={selectedCustomer}
        onBack={handleBackToHeader}
        onSave={handleSaveQuote}
      />
    );
  }

  // Step 2: Header Details
  if (currentStep === 'quote-creation' && selectedCustomer) {
    return (
      <QuoteHeaderScreen 
        customer={selectedCustomer}
        onBack={handleBackToCustomerSelection}
        onSave={handleContinueToLineItems}
      />
    );
  }

  // Step 1: Customer Selection
  return (
    <Box p="md">
      <Paper p="lg" shadow="sm" mb="md">
        <Stack spacing="md">
          <Group position="apart" align="center">
            <Title order={2}>Create New Quote</Title>
            <Badge color="gray" size="lg">Step 1: Select Customer</Badge>
          </Group>
          
          <Group position="apart" align="center">
            {/* Selected customer info on left side */}
            {selectedCustomer ? (
              <Group spacing="md" align="center">
                <Text size="sm" color="blue" weight={500}>Selected:</Text>
                <Box>
                  <Text weight={600}>{selectedCustomer.customerName}</Text>
                  <Text size="xs" color="dimmed">{selectedCustomer.customerNumber} • {selectedCustomer.region}</Text>
                </Box>
                <Button 
                  variant="subtle" 
                  size="xs"
                  onClick={() => setSelectedCustomer(null)}
                >
                  Clear
                </Button>
              </Group>
            ) : (
              <Text size="sm" color="dimmed">No customer selected</Text>
            )}
            
            {/* Actions on right side */}
            <Group spacing="sm">
              {selectedCustomer && (
                <Button 
                  onClick={() => setCurrentStep('quote-creation')}
                >
                  Next: Create Quote
                </Button>
              )}
            </Group>
          </Group>
        </Stack>
      </Paper>

      {/* Always show the customer search table */}
      <CustomerSearchDialog 
        embedded={true}
        onCustomerSelect={handleCustomerSelect}
        selectedCustomer={selectedCustomer}
      />
    </Box>
  );
};

export default QuoteCreationWizard;